import { updater } from "../../utils/updater.js";
import apiRequest from "../../utils/ApiRequest/apiRequest.js";
import { PickerStateI } from "./pickerContext.ts";

// Picker API factory.
export function pickerFactory({
  state,
  setState,
}: {
  state: PickerStateI;
  setState: (value: any) => PickerStateI;
}) {
  const updateState = updater(setState);

  const getPickerMailList = () => state.pickerMailList;

  const getIsFetchingPickerData = () => state.isPickerFetching;

  const getProjectsQuantity = () => state.projectsQuantity;

  const getCurrentPage = () => state.currentPage;

  const getIFrameLink = () => state.iFrameLink;

  const setIFrameLink = (link: string) => {
    updateState((draft: PickerStateI) => {
      draft.iFrameLink = link;
    });
  };

  const toggleSort = () => {
    updateState((draft: PickerStateI) => {
      draft.sort = state.sort === "asc" ? "desc" : "asc";
    });
  };

  const getSort = () => state.sort;

  const setCurrentPage = (number: number) =>
    updateState((draft: PickerStateI) => {
      draft.currentPage = number;
    });

  const incPageNumber = () => {
    if (
      state.projectsQuantity.totalActive < state.currentPage * 10 ||
      state.isPickerFetching
    )
      return;
    updateState((draft: PickerStateI) => {
      draft.currentPage = state.currentPage + 1;
    });
  };

  const decPageNumber = () => {
    if (state.currentPage === 1 || state.isPickerFetching) return;
    updateState((draft: PickerStateI) => {
      draft.currentPage = state.currentPage - 1;
    });
  };

  const setIsFetchingPickerData = (value: boolean) => {
    updateState((draft: PickerStateI) => {
      draft.isPickerFetching = value;
    });
  };

  const setSearchTerm = (value: string) => {
    updateState((draft: PickerStateI) => {
      draft.searchTerm = value;
    });
    setCurrentPage(1);
  };

  const getSearchTerm = () => state.searchTerm;

  const removeProjectById = (galleryId: string) => {
    updateState((draft: PickerStateI) => {
      draft.pickerMailList = state.pickerMailList.filter(
        (project) => project.PickerGalleryID !== galleryId
      );
      draft.projectsQuantity.totalActive--;
    });
  };

  const fetchPickerMailList = async (UserEncryptedId: string) => {
    setIsFetchingPickerData(true);
    const isArchived =
      new URLSearchParams(window.location.search).get("p") === "archived";

    try {
      const response = await apiRequest(
        "Picker",
        isArchived
          ? "ExternalPostRequestGetArchiveProjectsList"
          : "ExternalPostRequestGetProjectsList",
        {
          UserEncryptedId,
        },
        "POST"
      );
      if (response.status === "success") {
        updateState((draft: PickerStateI) => {
          draft.pickerMailList = response.data || [];
        });
      }
    } catch (e) {
      console.log(e);
    }
    setIsFetchingPickerData(false);
  };

  const fetchPickerProjectById = async (
    galleryId: string,
    UserEncryptedId: string
  ) => {
    try {
      const response = await apiRequest(
        "Picker",
        `ExternalPostRequestGetProjectsList`,
        {
          UserEncryptedId,
          Sid: galleryId,
        },
        "POST"
      );
      if (response.status === "success") {
        const replacedItemIndex = state.pickerMailList.findIndex(
          (item) => item.PickerGalleryID === galleryId
        );
        if (response.data?.[0]) {
          updateState((draft: PickerStateI) => {
            draft.pickerMailList[replacedItemIndex] = response.data[0];
          });
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const removeProject = async (projectEncryptedId: string) => {
    try {
      const response = await apiRequest(
        "Picker",
        "ExternalPostRequestRemoveProject",
        {
          order_num: projectEncryptedId,
          removeType: "remove",
          underMode: "jm",
        },
        "POST"
      );
      if (response.status === "success") {
        updateState((draft: PickerStateI) => {
          draft.pickerMailList = draft.pickerMailList.filter(
            (project) => project.PickerProjectID !== projectEncryptedId
          );
          draft.projectsQuantity.totalActive--;
        });
        return true;
      } else {
        return false;
      }
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const unarchiveProject = async (projectEncryptedId: string) => {
    try {
      const response = await apiRequest(
        "Picker",
        "ExternalPostRequestUnArchiveProject",
        {
          order_num: projectEncryptedId,
          underMode: "jm",
        },
        "POST"
      );
      if (response.status === "success") {
        updateState((draft: PickerStateI) => {
          draft.pickerMailList = draft.pickerMailList.filter(
            (project) => project.PickerGalleryID !== projectEncryptedId
          );
          draft.projectsQuantity.totalActive++;
          draft.projectsQuantity.totalArchive--;
        });
        return true;
      } else {
        return false;
      }
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const archiveProject = async (projectEncryptedId: string) => {
    try {
      const response = await apiRequest(
        "Picker",
        "ExternalPostRequestRemoveProject",
        {
          order_num: projectEncryptedId,
          removeType: "archive",
          underMode: "jm",
        },
        "POST"
      );
      if (response.status === "success") {
        updateState((draft: PickerStateI) => {
          draft.pickerMailList = draft.pickerMailList.filter(
            (project) => project.PickerGalleryID !== projectEncryptedId
          );
          draft.projectsQuantity.totalActive--;
          draft.projectsQuantity.totalArchive++;
        });
        return true;
      } else {
        return false;
      }
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const fetchPickerProjectsCount = async (UserEncryptedId: string) => {
    try {
      const response = await apiRequest(
        "Picker",
        "ExternalPostRequestGetProjectsCount",
        {
          UserEncryptedId,
        },
        "POST"
      );
      if (response.status === "success") {
        updateState((draft: PickerStateI) => {
          draft.projectsQuantity = response.data;
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const getPickerProjectsCount = () => {
    return state.projectsQuantity;
  };

  return {
    fetchPickerMailList,
    fetchPickerProjectById,
    removeProject,
    archiveProject,
    unarchiveProject,
    getPickerMailList,
    getIsFetchingPickerData,
    getProjectsQuantity,
    getCurrentPage,
    setCurrentPage,
    incPageNumber,
    decPageNumber,
    getSearchTerm,
    setSearchTerm,
    getIFrameLink,
    setIFrameLink,
    removeProjectById,
    toggleSort,
    getSort,
    fetchPickerProjectsCount,
    getPickerProjectsCount,
  };
}

export default pickerFactory;
